import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TableData } from '@uikit/Tables/types';
import { filterBodyTypes, monitoringApi } from '@store/rtk/monitoring';
import { detailSegmentDataType } from '@component/Monitoring/MonitoringSegmentDetail/MonitoringSegmentTabs/SegmentDetailSegment/constants';
import { isValidJson } from '@utils/helpers';
import { removeSeconds } from '@component/RidesPage/RidesTable/constants';
import { removeCounty } from '@src/utils';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

type MonitoringSliceType = {
  monitoringTableData: TableData;
  monitoringFilters: filterBodyTypes;
  monitoringSearchQuery: string;
  segmentDetailData: detailSegmentDataType | null;
  rideOrderId: string;
};

const { monitoringTableColumns } = TABLE_COLUMNS;

const initialState: MonitoringSliceType = {
  monitoringTableData: {
    columns: monitoringTableColumns,
    rows: [],
    size: 10,
  },
  monitoringFilters: {},
  monitoringSearchQuery: '',
  segmentDetailData: null,
  rideOrderId: '',
};

export const monitoringSlice = createSlice({
  name: 'monitoring',
  initialState,
  reducers: {
    setRideOrderId: (
      state: MonitoringSliceType,
      { payload }: { payload: string }
    ) => {
      state.rideOrderId = payload;
    },
    setMonitoringFilters: (
      state: MonitoringSliceType,
      { payload }: { payload: filterBodyTypes }
    ) => {
      state.monitoringFilters = payload;
      // TODO check this part!!
      // state.monitoringFilters = {
      //   ...state.monitoringFilters,
      //   ...payload,
      // };
    },
    clearMonitoringFilters: (
      state: MonitoringSliceType,
      { payload }: { payload: string }
    ) => {
      const filters = { ...state.monitoringFilters };
      delete filters[payload];
      state.monitoringFilters = { ...filters };
    },
    setMonitoringSearchQuery: (
      state: MonitoringSliceType,
      { payload }: { payload: string }
    ) => {
      state.monitoringSearchQuery = payload;
    },
    setFilterRequestDate: (state, { payload }) => {
      state.monitoringFilters.rideRequestDate = payload;
    },
    updateWillCallPickupTime: (
      state: MonitoringSliceType,
      {
        payload,
      }: PayloadAction<{ segmentId: number; pickupTime: string }>
    ) => {
      state.monitoringTableData.rows = state.monitoringTableData.rows.map(
        (row) =>
          String(row.id) === String(payload.segmentId)
            ? { ...row, pickUpTime: payload.pickupTime }
            : row
      );

      if (
        state.segmentDetailData &&
        String(state.segmentDetailData.id) === String(payload.segmentId)
      ) {
        state.segmentDetailData.scheduledPickupTime = payload.pickupTime;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      monitoringApi.endpoints.getSegmentDetailsById.matchFulfilled,
      (state, { payload }) => {
        if (payload) {
          state.segmentDetailData = {
            ...payload,
            scheduledAppointmentTime: payload?.scheduledAppointmentTime
              ? removeSeconds(payload?.scheduledAppointmentTime)
              : null,
            scheduledPickupTime: payload?.scheduledPickupTime
              ? removeSeconds(payload?.scheduledPickupTime)
              : null,
            driver: isValidJson(payload?.driver)
              ? JSON.parse(payload?.driver)
              : {},
            vehicle: isValidJson(payload?.vehicle)
              ? JSON.parse(payload?.vehicle)
              : {},
            rideRemoteId: payload?.id || '',
          };
        }
      }
    );
    builder.addMatcher(
      monitoringApi.endpoints.getRidesByAssigned.matchFulfilled,
      (state, { payload }) => {
        if (payload) {
          if (!payload.totalElements) {
            state.monitoringTableData.rows = [];
          }
          let rideOrderConfirmationCode = '';
          let segmentNumber = 0;

          const newItems = payload.content.map((el) => {
            if (el.rideOrderConfirmationCode === rideOrderConfirmationCode) {
              segmentNumber += 1;
            } else {
              rideOrderConfirmationCode = el.rideOrderConfirmationCode;
              segmentNumber = 1;
            }

            return {
              ...el,
              member: el.member,
              service: el.service,
              pickUpTime: el.pickUpTime,
              county: removeCounty(el.pickUpCounty),
              puCity: el.pickUpCityZipcode,
              doCity: el.dropOfCityZipcode,
              miles: el.miles,
              rideOrderConfirmationCode: el.rideOrderConfirmationCode,
              status: el.status,
              statusId: el.statusId,
              contractor: el.contractor,
              segment: segmentNumber,
            };
          });

          state.monitoringTableData = {
            ...state.monitoringTableData,
            rows:
              payload?.page === 0
                ? newItems
                : [...state.monitoringTableData.rows, ...newItems],
            ...payload,
            requestTablePageChange: monitoringApi.endpoints.getRidesByAssigned,
          };
        }
      }
    );
    // builder.addMatcher(
    //   monitoringApi.endpoints.getRidesByAssigned.matchRejected,
    //   (state) => {
    //     state.monitoringTableData.rows = [];
    //   }
    // );
  },
});

export const {
  setRideOrderId,
  setMonitoringFilters,
  clearMonitoringFilters,
  setMonitoringSearchQuery,
  setFilterRequestDate,
  updateWillCallPickupTime,
} = monitoringSlice.actions;

export const selectMonitoring = (state: { monitoring: MonitoringSliceType }) =>
  state.monitoring;
export const monitoringReducer = monitoringSlice.reducer;
